import React, { forwardRef, FunctionComponent, Ref, useContext } from 'react';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';

import { MessageType } from '@savgroup-front-common/types';

import { safeFormattedIntlString } from '../../formatters';

import { $Link, $RouterLink } from './Link.styles';

type LinkProps = {
  href?: string;
  rel?: string;
  isExternal?: boolean;
  dataTestId?: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  forwardedRef?: Ref<HTMLAnchorElement>;
  target?: string;
  isWrap?: boolean;
  isInline?: boolean;
  icon?: React.ReactElement;
  className?: string;
  isNaked?: boolean;
  disabled?: boolean;
  download?: boolean | string;
  ariaLabel?: MessageType | string;
  title?: string;
};

const Link: FunctionComponent<React.PropsWithChildren<LinkProps>> = ({
  href = '',
  isExternal = false,
  children,
  dataTestId = null,
  onClick,
  forwardedRef,
  isWrap = false,
  icon = undefined,
  className,
  isInline,
  rel,
  target,
  isNaked,
  disabled,
  download = false,
  ariaLabel,
  ...props
}) => {
  // Check if we're inside a Router context
  const navigation = useContext(NavigationContext);
  const hasRouterContext = navigation !== null;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (disabled) {
      e.preventDefault();

      return undefined;
    }
    if (onClick) {
      onClick(e);

      return undefined;
    }
    e.stopPropagation();

    return undefined;
  };

  const commonProps = {
    ...props,
    'data-testid': dataTestId,
    $isWrap: isWrap,
    $isInline: isInline,
    ref: forwardedRef,
    onClick: handleClick,
    className: className,
    download: download,
    'aria-label': ariaLabel ? safeFormattedIntlString(ariaLabel) : undefined,
  };

  // If it's an external link or we don't have a router context, use a regular anchor tag
  if (isExternal || !hasRouterContext) {
    return (
      <$Link {...commonProps} href={href} rel={rel} target={target}>
        {children}
        {icon && icon}
      </$Link>
    );
  }

  // Otherwise, use the RouterLink
  return (
    <$RouterLink {...commonProps} to={href} $isNaked={isNaked}>
      {children}
      {icon && icon}
    </$RouterLink>
  );
};

export default forwardRef<
  HTMLAnchorElement,
  Omit<React.PropsWithChildren<LinkProps>, 'forwardedRef'>
>((props, ref) => <Link forwardedRef={ref} {...props} />);
